"use client";

import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { apiPost, apiGet } from "@/integrations/api/client";

interface AuthUser {
  id: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: AuthUser | null;
  session: { token: string } | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null; isFirstAdmin?: boolean }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<{ token: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setLoading(false);
      return;
    }

    apiGet<{ user: AuthUser }>("/auth/session")
      .then(({ user: u }) => {
        setUser(u);
        setSession({ token });
        setIsAdmin(u.role === "admin");
      })
      .catch(() => {
        localStorage.removeItem("auth_token");
      })
      .finally(() => setLoading(false));
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const data = await apiPost<{ token: string; user: AuthUser }>(
        "/auth/login",
        { email, password }
      );
      localStorage.setItem("auth_token", data.token);
      setUser(data.user);
      setSession({ token: data.token });
      setIsAdmin(data.user.role === "admin");
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const data = await apiPost<{
        token: string;
        user: AuthUser;
        isFirstAdmin: boolean;
      }>("/auth/register", { email, password });
      localStorage.setItem("auth_token", data.token);
      setUser(data.user);
      setSession({ token: data.token });
      setIsAdmin(data.user.role === "admin");
      return { error: null, isFirstAdmin: data.isFirstAdmin };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    localStorage.removeItem("auth_token");
    setUser(null);
    setSession(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
